import React, { useState, useEffect, memo, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Camera,
  Check,
  X,
  AlertTriangle,
  AlertCircle,
  ChevronRight,
  FileText,
  Upload,
  Shield
} from 'lucide-react';
import {
  WORKFLOW_STAGES,
  EVIDENCE_PROMPTS,
  REQUIREMENT_LEVELS,
  getStageRequirements,
  checkEvidenceCompleteness,
  getCategoryLabel
} from '@/config/evidenceRules';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Log workflow enforcement event to backend audit
 */
async function logEnforcementEvent(jobId, eventData, token) {
  try {
    await axios.post(
      `${API}/api/phs/jobs/${jobId}/workflow-enforcement-log`,
      eventData,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  } catch (err) {
    console.error('Failed to log enforcement event:', err);
  }
}

/**
 * Evidence Prompt Dialog
 * Shows contextual evidence requirements before workflow transitions
 */
function EvidencePromptDialog({
  open,
  onOpenChange,
  job,
  stage,
  onProceed,
  onUploadEvidence
}) {
  const [acknowledged, setAcknowledged] = useState({});
  const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
  
  // Derive computed values (may be empty if no job/stage)
  const jobType = job?.job_type || job?.category || 'default';
  const evidence = job?.evidence_files || [];
  const requirements = job && stage ? getStageRequirements(jobType, stage) : {};
  const completeness = job && stage ? checkEvidenceCompleteness(evidence, jobType, stage) : { canProceed: true, hardBlocks: [], softWarnings: [] };
  const promptConfig = EVIDENCE_PROMPTS[stage] || {};
  
  // Reset acknowledgments when dialog opens
  useEffect(() => {
    if (open) {
      setAcknowledged({});
    }
  }, [open]);
  
  // Filter prompts to show only relevant ones based on requirements
  const relevantPrompts = (promptConfig.prompts || []).filter(
    prompt => requirements[prompt.category]
  );
  
  // Check if item is complete
  const isItemComplete = useCallback((category) => {
    return evidence.some(e => e.category === category);
  }, [evidence]);
  
  // Check if item is required
  const isRequired = useCallback((category) => {
    return requirements[category] === REQUIREMENT_LEVELS.REQUIRED;
  }, [requirements]);
  
  // Check if item is acknowledged (soft warning dismissed)
  const isAcknowledged = useCallback((category) => {
    return acknowledged[category] === true;
  }, [acknowledged]);
  
  // Toggle acknowledgment with logging
  const toggleAcknowledge = useCallback((category) => {
    setAcknowledged(prev => ({ ...prev, [category]: !prev[category] }));
  }, []);
  
  // Check if can proceed
  const canProceed = completeness.canProceed || 
    completeness.hardBlocks.every(block => isItemComplete(block.category));
  
  // Check if all soft warnings are acknowledged
  const allWarningsAcknowledged = completeness.softWarnings.every(
    warn => isItemComplete(warn.category) || isAcknowledged(warn.category)
  );
  
  // Get list of bypassed warnings
  const getBypassedWarnings = useCallback(() => {
    return completeness.softWarnings
      .filter(warn => !isItemComplete(warn.category) && isAcknowledged(warn.category))
      .map(warn => warn.category);
  }, [completeness.softWarnings, isItemComplete, isAcknowledged]);
  
  const handleProceed = useCallback(async () => {
    const bypassedWarnings = getBypassedWarnings();
    
    // Log enforcement event if warnings were bypassed
    if (bypassedWarnings.length > 0 && job?.id) {
      logEnforcementEvent(job.id, {
        event_type: 'soft_warning_bypassed',
        action_attempted: stage,
        missing_evidence: completeness.softWarnings.map(w => w.category),
        warnings_bypassed: bypassedWarnings,
        outcome: 'proceeded',
        notes: `User acknowledged and bypassed warnings for: ${bypassedWarnings.join(', ')}`
      }, token);
    }
    
    onProceed?.();
    onOpenChange(false);
  }, [job?.id, stage, completeness.softWarnings, getBypassedWarnings, token, onProceed, onOpenChange]);
  
  // Early return AFTER all hooks
  if (!job || !stage) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="evidence-prompt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-teal-600" />
            {promptConfig.title || 'Evidence Check'}
          </DialogTitle>
          <DialogDescription>
            {promptConfig.subtitle || 'Verify required evidence before proceeding'}
          </DialogDescription>
        </DialogHeader>
        
        {/* Evidence Checklist */}
        <div className="space-y-3 py-4">
          {relevantPrompts.map((prompt) => {
            const complete = isItemComplete(prompt.category);
            const required = isRequired(prompt.category);
            const acked = isAcknowledged(prompt.category);
            
            return (
              <div 
                key={prompt.category}
                className={`flex items-start gap-3 p-3 rounded-xl border transition ${
                  complete 
                    ? 'bg-emerald-50 border-emerald-200' 
                    : required
                    ? 'bg-red-50 border-red-200'
                    : acked
                    ? 'bg-slate-50 border-slate-200'
                    : 'bg-amber-50 border-amber-200'
                }`}
                data-testid={`evidence-prompt-${prompt.category}`}
              >
                {/* Status Icon */}
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  complete 
                    ? 'bg-emerald-500 text-white' 
                    : required
                    ? 'bg-red-500 text-white'
                    : acked
                    ? 'bg-slate-400 text-white'
                    : 'bg-amber-500 text-white'
                }`}>
                  {complete ? (
                    <Check className="h-4 w-4" />
                  ) : required ? (
                    <X className="h-4 w-4" />
                  ) : acked ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <AlertTriangle className="h-4 w-4" />
                  )}
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${
                      complete ? 'text-emerald-700' : required ? 'text-red-700' : 'text-slate-700'
                    }`}>
                      {prompt.label}
                    </span>
                    {required && !complete && (
                      <Badge variant="destructive" className="text-[9px] px-1.5 py-0">Required</Badge>
                    )}
                    {!required && !complete && !acked && (
                      <Badge variant="secondary" className="text-[9px] px-1.5 py-0 bg-amber-100 text-amber-700">Recommended</Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{prompt.description}</p>
                  
                  {/* Actions for incomplete items */}
                  {!complete && (
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => onUploadEvidence?.(prompt.category)}
                      >
                        <Upload className="h-3 w-3 mr-1" />
                        Upload
                      </Button>
                      {!required && !acked && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs text-slate-500"
                          onClick={() => toggleAcknowledge(prompt.category)}
                        >
                          Skip
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Summary */}
        <div className="flex items-center justify-between py-2 border-t border-slate-100">
          <div className="text-xs text-slate-500">
            {completeness.completedRequired}/{completeness.totalRequired} required • 
            {completeness.completedRecommended}/{completeness.totalRecommended} recommended
          </div>
          {!canProceed && (
            <Badge variant="destructive" className="text-[10px]">
              <AlertCircle className="h-3 w-3 mr-1" />
              Cannot proceed
            </Badge>
          )}
        </div>
        
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleProceed}
            disabled={!canProceed || !allWarningsAcknowledged}
            className="bg-teal-600 hover:bg-teal-700"
          >
            {canProceed && allWarningsAcknowledged ? (
              <>
                Proceed
                <ChevronRight className="h-4 w-4 ml-1" />
              </>
            ) : (
              'Complete Requirements'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default memo(EvidencePromptDialog);
